import _ from 'lodash';
import { addToRootSaga } from 'store';
import { call, put, select, all, takeLatest } from 'redux-saga/effects';
import {
  createNewCatalog,
  getCatalogs,
  deleteCatalog,
  deleteCatalogItem,
  getCatalogPaginatedItems,
} from 'api/CatalogAPI';
import { ASCENDING } from 'constants/sortDirections';
import { addPositiveToast, addNegativeToast } from 'modules/toastContainer';
import { getErrorResponse } from 'utils/xhr';

import {
  fetchCatalogsResponse,
  fetchCatalogsError,
  removeCatalogResponse,
  removeCatalogError,
  removeCatalogItemResponse,
  removeCatalogItemError,
  createNewCatalogResponse,
  createNewCatalogError,
  fetchCatalogNamesResponse,
  fetchCatalogNamesError,
  fetchPaginatedCatalogItemsResponse,
  fetchPaginatedCatalogItemsError,
} from './actions';
import * as actionTypes from './actionTypes';
import {
  getCatalogDataList,
  getCatalogDataObj,
  getCatalogName,
  getCatalogPending,
  getCatalogPendingDelete,
} from './selectors';
import {
  transformCatalogDataObj,
  transformCatalogDataList,
  transformCatalogItemsDataList,
  mergeCatalogItemsAndReplaceValues,
} from './helpers';

// this is basically the same as fetchCatalogs but it doesn't update the current dataList
// THIS WILL JUST USE DEFAULTS FOR page, pageSize, etc.
// eventually it might be good to create a separate endpoint for just fetching the catalog names to use in this saga
// and another endpoint for fetching paginated, filtered catalogs for the endpoint used in fetchCatalogs
export function* fetchCatalogNames() {
  try {
    const { data } = yield call(getCatalogs);
    const dataObj = yield select(getCatalogDataObj);
    yield put(
      fetchCatalogNamesResponse({
        ...transformCatalogDataObj(data),
        ...dataObj,
      }),
    );
  } catch (error) {
    yield put(fetchCatalogNamesError(error.toString()));
  }
}

export function* fetchCatalogs() {
  try {
    const { data } = yield call(getCatalogs);
    const dataList = transformCatalogDataList(data);
    let pending = yield select(getCatalogPending);
    pending = {
      ...pending,
      tables: _.differenceBy(pending.tables || [], dataList, 'name'),
    };
    yield put(fetchCatalogsResponse(dataList, transformCatalogDataObj(data), pending));
  } catch (error) {
    yield put(fetchCatalogsError(error));
  }
}

export function* fetchPaginatedCatalogItems({ tableName, filters }) {
  try {
    // getCatalogPaginatedItems takes entry # and size; entries start with 0
    // const from = (filters.page - 1) * filters.pageSize;
    const page = _.get(filters, 'page', 1);
    const pageSize = _.get(filters, 'pageSize', 10);
    const orderBy = _.get(filters, 'orderBy', null);
    const sortAscending = _.get(filters, 'sorting', null) === ASCENDING;
    const params = { tableName, page, pageSize, orderBy, sortAscending };
    const { data } = yield call(getCatalogPaginatedItems, params);
    const sizeInBytes = data[2] ?? -1;
    const total = data[1];
    const resp = data[0];
    // replace dataList with modified dataList
    const dataList = transformCatalogItemsDataList(resp);
    // replace dataObj with modified dataObj
    const dataObj = yield select(getCatalogDataObj);
    // this will replace the values of each item rather than deep merging
    const mergedItems = mergeCatalogItemsAndReplaceValues({ dataObj, tableName, resp });

    // update pending
    let pending = yield select(getCatalogPending);
    pending = {
      ...pending,
      [tableName]: _.differenceBy(pending[tableName] || [], dataList, 'name'),
    };

    yield put(
      fetchPaginatedCatalogItemsResponse(
        dataList,
        mergedItems,
        tableName,
        filters,
        pending,
        total,
        sizeInBytes,
      ),
    );
  } catch (error) {
    yield put(fetchPaginatedCatalogItemsError(error.toString()));
  }
}

export function* removeCatalog({ tableName }) {
  try {
    const { data } = yield call(deleteCatalog, tableName);
    const { catalogTableDataList, catalogTableDataObj, catalogTableName } = yield all({
      catalogTableDataList: select(getCatalogDataList),
      catalogTableDataObj: select(getCatalogDataObj),
      catalogTableName: select(getCatalogName),
    });

    let dataList = [...catalogTableDataList];
    let dataObj = { ...catalogTableDataObj };
    // check to make sure we're updating the right dataList
    if (!catalogTableName) {
      dataList = _.reject(dataList, { name: tableName });
      dataObj = _.omit(dataObj, tableName);
    }
    yield put(removeCatalogResponse(dataList, dataObj, data));
    yield put(addPositiveToast(data));
  } catch (error) {
    const err = getErrorResponse(error);
    yield put(removeCatalogError(err));
    const message = `Could not delete catalog. ${err}`;
    yield put(addNegativeToast(message));
  }
}

export function* removeCatalogItem({ tableName, tableKey }) {
  try {
    const { data } = yield call(deleteCatalogItem, tableName, tableKey);
    const { catalogTableName, catalogPendingDelete } = yield all({
      catalogTableName: select(getCatalogName),
      catalogPendingDelete: select(getCatalogPendingDelete),
    });

    let pendingDelete = { ...catalogPendingDelete };

    // TODO does this check need to happen?
    if (catalogTableName) {
      pendingDelete = {
        ...pendingDelete,
        [tableName]: {
          ..._.get(pendingDelete, tableName, {}),
          [tableKey]: tableKey,
        },
      };
    }
    yield put(removeCatalogItemResponse(pendingDelete));
    yield put(addPositiveToast(data));
  } catch (error) {
    const err = getErrorResponse(error);
    yield put(removeCatalogItemError(err));
    const message = `Could not delete item. ${err}`;
    yield put(addNegativeToast(message));
  }
}

export function* createNewCatalogRequest({ catalogName }) {
  try {
    const requestMessage = `Received request to create new catalog ${catalogName}`;
    yield put(addPositiveToast(requestMessage));

    yield call(createNewCatalog, catalogName);

    yield* fetchCatalogs();

    let pending = yield select(getCatalogPending);
    const catalogTableDataObj = yield select(getCatalogDataObj);
    const isCatalogNamePresent = catalogName in catalogTableDataObj;
    // sometimes tables get added pretty much immediately
    pending = {
      ...pending,
      tables: isCatalogNamePresent
        ? pending.tables
        : _.unionBy(pending.tables, [{ name: catalogName }], 'name'),
    };

    yield put(createNewCatalogResponse(pending, catalogName));

    const responseMessage = `Successfully created catalog ${catalogName}`;
    yield put(addPositiveToast(responseMessage));
  } catch (error) {
    const msg = getErrorResponse(error);
    yield put(createNewCatalogError(msg));
    yield put(addNegativeToast(msg));
  }
}

addToRootSaga([
  takeLatest(actionTypes.FETCH_CATALOGS_REQUEST, fetchCatalogs),
  takeLatest(actionTypes.REMOVE_CATALOG_REQUEST, removeCatalog),
  takeLatest(actionTypes.REMOVE_CATALOG_ITEM_REQUEST, removeCatalogItem),
  takeLatest(actionTypes.CREATE_CATALOG_REQUEST, createNewCatalogRequest),
  takeLatest(actionTypes.FETCH_CATALOG_NAMES_REQUEST, fetchCatalogNames),
  takeLatest(actionTypes.FETCH_PAGINATED_CATALOG_ITEMS_REQUEST, fetchPaginatedCatalogItems),
]);
